// The staged "Bases" builder: a master size, an optional base, an optional
// topper, and any number of placed bits.
//
// BuildState owns all form state (surviving step switches, which only hide/show
// DOM) and is the single source of the `{"kind":"build",...}` scene JSON the
// mesh and cart paths share. The stepper chrome (headers, step containers) is
// wired here against markup already in index.html.
import {
  FormKind,
  ParamsForm,
  bit_name,
  topper_name,
  topper_options,
  bit_options,
} from "../pkg/viewer.js";
import { wireParams, applyParams, formProxy, paramMap } from "./form.js";

export type BuildCallbacks = {
  // Rebuild the mesh; `refit` reframes the camera (selection change, add/remove
  // a bit, first show) versus a plain param tweak.
  requestMesh: (refit: boolean) => void;
};

// A build as scene JSON: master size at top level, base/topper omitted when
// None, one entry per bit with its params and placement.
export type BuildScene = {
  kind: "build";
  size: number;
  base?: { params: Record<string, number> };
  topper?: { archetypeId: number; params: Record<string, number> };
  bits: {
    archetypeId: number;
    params: Record<string, number>;
    placement: Record<string, number>;
  }[];
};

// One placed bit: its archetype params plus a placement (pose on the surface).
type BitCard = {
  card: HTMLElement;
  paramsEl: HTMLElement;
  placementEl: HTMLElement;
  bitId: number;
  archetype: ParamsForm;
  placement: ParamsForm;
};

export type BuildElements = {
  masterSize: HTMLElement;
  baseChoice: HTMLSelectElement;
  baseParams: HTMLElement;
  topperChoice: HTMLSelectElement;
  topperParams: HTMLElement;
  bitChoice: HTMLSelectElement;
  addBit: HTMLButtonElement;
  bitList: HTMLElement;
};

export class BuildState {
  private cb: BuildCallbacks;

  // Master size (drives base diameter + bit radius range; the topper follows
  // the surface it is built onto).
  private masterSize: ParamsForm;

  // Base: enabled + its params (diameter omitted; master size supplies it).
  private baseEnabled = true;
  private baseForm: ParamsForm;

  // Topper: chosen archetype id (null = None) + its params.
  private topperId: number | null = null;
  private topperForm: ParamsForm | null = null;

  private bits: BitCard[] = [];

  // DOM refs.
  private els: BuildElements;

  constructor(els: BuildElements, cb: BuildCallbacks) {
    this.cb = cb;
    this.els = els;

    this.masterSize = new ParamsForm(FormKind.MasterSize);
    this.baseForm = new ParamsForm(FormKind.Base);

    this.wireMasterSize();
    this.wireBase();
    this.wireTopper();
    this.wireBits();
  }

  // --- Master size -------------------------------------------------------

  private wireMasterSize() {
    this.renderMasterSize();
    wireParams(this.els.masterSize, formProxy(() => this.masterSize), {
      // Size changes the base/topper diameter and the bits' legal radius, so
      // reframe and retarget the placement sliders.
      onChange: () => {
        this.retargetRadiusSliders();
        this.cb.requestMesh(true);
      },
    });
  }

  private renderMasterSize() {
    this.els.masterSize.innerHTML = this.masterSize.form_html();
  }

  private size(): number {
    return paramMap(this.masterSize).size;
  }

  // Placement radius runs to the build's edge: max = size / 2. Update every
  // bit card's radius slider/number max and clamp any value past it.
  private retargetRadiusSliders() {
    const max = this.size() / 2;
    for (const bit of this.bits) {
      // The placement's radius, not any radius an archetype of its own has.
      for (const input of bit.placementEl.querySelectorAll<HTMLInputElement>(
        'input[data-param="radius"]',
      )) {
        input.max = String(max);
        if (Number(input.value) > max) input.value = String(max);
      }
      const radius = paramMap(bit.placement).radius;
      if (radius > max) bit.placement.set_param("radius", max);
    }
  }

  // --- Base --------------------------------------------------------------

  private wireBase() {
    this.renderBaseParams();
    this.els.baseChoice.value = this.baseEnabled ? "base" : "none";
    this.els.baseChoice.addEventListener("change", () => {
      this.baseEnabled = this.els.baseChoice.value === "base";
      this.renderBaseParams();
      this.cb.requestMesh(true);
    });
    wireParams(this.els.baseParams, formProxy(() => this.baseForm), {
      onChange: () => this.cb.requestMesh(false),
    });
  }

  private renderBaseParams() {
    // Diameter is supplied by the master size, so omit it here.
    this.els.baseParams.innerHTML = this.baseEnabled
      ? this.baseForm.form_html_omitting("diameter")
      : "";
  }

  // --- Topper ------------------------------------------------------------

  private wireTopper() {
    this.els.topperChoice.innerHTML =
      `<option value="none">None</option>` + topper_options();
    this.els.topperChoice.value = "none";
    this.renderTopperParams();
    this.els.topperChoice.addEventListener("change", () => {
      const value = this.els.topperChoice.value;
      if (value === "none") {
        this.topperId = null;
        this.topperForm = null;
      } else {
        this.topperId = Number(value);
        this.topperForm = ParamsForm.new_topper(this.topperId);
      }
      this.renderTopperParams();
      this.cb.requestMesh(true);
    });
    wireParams(this.els.topperParams, formProxy(() => this.topperForm), {
      onChange: () => this.cb.requestMesh(false),
    });
  }

  private renderTopperParams() {
    this.els.topperParams.innerHTML = this.topperForm
      ? this.topperForm.form_html()
      : "";
  }

  // --- Bits --------------------------------------------------------------

  private wireBits() {
    this.els.bitChoice.innerHTML = bit_options();
    this.els.addBit.addEventListener("click", () =>
      this.addBit(Number(this.els.bitChoice.value)),
    );
  }

  addBit(bitId: number) {
    this.renderBitParams(this.createBit(bitId));
    this.retargetRadiusSliders();
    this.cb.requestMesh(true);
  }

  // Build, append and wire a bit card, leaving the render, the slider retarget
  // and the mesh rebuild to the caller: a restore renders once its params are
  // applied, and costs one retarget and one rebuild for the whole scene.
  //
  // The placement starts where the bit itself asks to be put. That is a
  // snapshot taken here and then left alone: a card is created for one
  // archetype and keeps it, so this is the only place a placement is seeded,
  // and editing the bit's params afterwards does not move it. A pipe made much
  // fatter may want its exposure nudged by hand. A restore overwrites the
  // snapshot with the stored placement.
  private createBit(bitId: number): BitCard {
    const archetype = ParamsForm.new_bit(bitId);
    const placement = ParamsForm.new_placement(bitId);

    const card = document.createElement("div");
    card.className = "bit-card";

    const header = document.createElement("div");
    header.className = "bit-header";
    const title = document.createElement("span");
    title.textContent = `${bit_name(bitId)} ${this.bits.length + 1}`;
    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "bit-remove";
    remove.textContent = "×";
    remove.title = "Remove";
    header.append(title, remove);

    const paramsEl = document.createElement("div");
    paramsEl.className = "bit-params";

    const placementLabel = document.createElement("div");
    placementLabel.className = "bit-subhead";
    placementLabel.textContent = "Placement";
    const placementEl = document.createElement("div");
    placementEl.className = "bit-placement";

    card.append(header, paramsEl, placementLabel, placementEl);
    this.els.bitList.appendChild(card);

    const bit: BitCard = { card, paramsEl, placementEl, bitId, archetype, placement };
    this.bits.push(bit);

    wireParams(paramsEl, archetype, { onChange: () => this.cb.requestMesh(false) });
    wireParams(placementEl, placement, {
      onChange: () => this.cb.requestMesh(false),
    });
    remove.addEventListener("click", () => this.removeBit(bit));

    return bit;
  }

  private renderBitParams(bit: BitCard) {
    bit.paramsEl.innerHTML = bit.archetype.form_html();
    bit.placementEl.innerHTML = bit.placement.form_html();
  }

  private removeBit(bit: BitCard) {
    bit.card.remove();
    this.bits = this.bits.filter((b) => b !== bit);
    this.renumberBits();
    this.cb.requestMesh(true);
  }

  // Keep the visible "Wheel N" titles matching the list order after a removal.
  private renumberBits() {
    this.bits.forEach((bit, i) => {
      const title = bit.card.querySelector(".bit-header span");
      if (title)
        title.textContent = `${bit_name(bit.bitId)} ${i + 1}`;
    });
  }

  // --- Restore -----------------------------------------------------------

  // Restore the whole builder from a scene of the shape `sceneJson()` produces
  // (a cart item, or a shared string). Every form is rebuilt from scratch, so a
  // param the scene doesn't mention comes back at its default rather than
  // keeping the value the user happened to have; params this build no longer
  // has are skipped and archetypes it doesn't know are dropped, so an older
  // scene still loads. Re-rendering each form from fresh HTML shows the result.
  // The caller reframes: this leaves the mesh alone.
  loadScene(scene: BuildScene) {
    this.masterSize = new ParamsForm(FormKind.MasterSize);
    applyParams(this.masterSize, { size: scene.size });
    this.renderMasterSize();

    this.baseEnabled = scene.base != null;
    this.els.baseChoice.value = this.baseEnabled ? "base" : "none";
    this.baseForm = new ParamsForm(FormKind.Base);
    if (scene.base) applyParams(this.baseForm, scene.base.params);
    this.renderBaseParams();

    const topperId = scene.topper?.archetypeId;
    const hasTopper =
      topperId != null && hasOption(this.els.topperChoice, String(topperId));
    this.els.topperChoice.value = hasTopper ? String(topperId) : "none";
    this.topperId = hasTopper ? topperId : null;
    this.topperForm = hasTopper ? ParamsForm.new_topper(topperId) : null;
    if (this.topperForm && scene.topper) {
      applyParams(this.topperForm, scene.topper.params);
    }
    this.renderTopperParams();

    for (const bit of this.bits) bit.card.remove();
    this.bits = [];
    for (const spec of scene.bits) {
      if (!hasOption(this.els.bitChoice, String(spec.archetypeId))) continue;
      const bit = this.createBit(spec.archetypeId);
      applyParams(bit.archetype, spec.params);
      applyParams(bit.placement, spec.placement);
      this.renderBitParams(bit);
    }

    this.retargetRadiusSliders();
  }

  // --- Randomise ---------------------------------------------------------

  // Randomise the archetype form on step `index` (0 = Base, 1 = Topper); other
  // steps have no randomisable form and are left untouched.
  randomiseStep(index: number) {
    const seed = Math.random() * 2 ** 53;
    if (index === 0 && this.baseEnabled) {
      this.baseForm.randomize(seed);
      // Master size still owns the diameter, so keep it omitted after re-render.
      this.renderBaseParams();
      this.cb.requestMesh(true);
    } else if (index === 1 && this.topperForm) {
      this.topperForm.randomize(seed);
      this.renderTopperParams();
      this.cb.requestMesh(true);
    }
  }

  // --- Scene / export ----------------------------------------------------

  // The build as scene JSON: the single source of truth for the mesh path, the
  // cart's STL export, and share strings.
  sceneJson(): string {
    const scene: BuildScene = {
      kind: "build",
      size: this.size(),
      bits: this.bits.map((bit) => ({
        archetypeId: bit.bitId,
        params: paramMap(bit.archetype),
        placement: paramMap(bit.placement),
      })),
    };
    if (this.baseEnabled) scene.base = { params: paramMap(this.baseForm) };
    if (this.topperId != null && this.topperForm) {
      scene.topper = {
        archetypeId: this.topperId,
        params: paramMap(this.topperForm),
      };
    }
    return JSON.stringify(scene);
  }

  // A short human-readable summary of the build for the cart list and BOM, e.g.
  // "32mm base + dirt + 2 wheels". Not an identifier — the scene JSON is.
  label(): string {
    const parts: string[] = [`${formatNumber(this.size())}mm`];
    if (this.baseEnabled) parts.push("base");
    if (this.topperId != null && this.topperForm) {
      parts.push(topper_name(this.topperId).toLowerCase());
    }
    const counts = new Map<number, number>();
    for (const bit of this.bits) {
      counts.set(bit.bitId, (counts.get(bit.bitId) ?? 0) + 1);
    }
    for (const [bitId, count] of counts) {
      const name = bit_name(bitId).toLowerCase();
      parts.push(`${count} ${count === 1 ? name : `${name}s`}`);
    }
    return parts.join(" + ");
  }
}

// Whether a select offers `value`. A scene can name an archetype this build
// doesn't have, and building a form for an unknown id panics in wasm.
function hasOption(select: HTMLSelectElement, value: string): boolean {
  return [...select.options].some((option) => option.value === value);
}

// Trim trailing zeros for compact display (e.g. 32.0 -> "32", 2.50 -> "2.5").
function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  return String(Number(n.toFixed(4)));
}
